import React, { useMemo, useRef, useState, useEffect } from "react";
import { Dropdown, Input, Portal } from "semantic-ui-react";
import { flattenOptions, highlightMatch } from "./utils";
import MenuLevel from "./MenuLevel";
import { ClippedContent } from "../../atoms/ClippedContent";
import "./index.scss";

const MAX_HEIGHT = 450;
const VIEWPORT_PADDING = 8;

const DylFieldSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Select DYL Field",
    isError = false,
}) => {
    const wrapperRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [hoveredPath, setHoveredPath] = useState([]);
    const [submenuPositions, setSubmenuPositions] = useState({ left: "-10px" });
    const [menuPosition, setMenuPosition] = useState({});

    const treeOptions = useMemo(() => options, [options]);
    const searchableItems = useMemo(
        () => flattenOptions(treeOptions),
        [treeOptions]
    );

    const filtered = searchableItems.filter((item) =>
        item.display.toLowerCase().includes(search.toLowerCase())
    );

    const selectedItem = searchableItems.find((item) => item.value === value);

    const updatePosition = () => {
        const rect = wrapperRef.current?.getBoundingClientRect();

        if (!rect) return;

        const below = window.innerHeight - rect.bottom - VIEWPORT_PADDING;

        const above = rect.top - VIEWPORT_PADDING;

        const openUp = below < 250 && above > below;

        const maxHeight = Math.min(MAX_HEIGHT, openUp ? above : below);

        setMenuPosition({
            width: rect.width,
            left: rect.left,
            top: openUp ? rect.top - maxHeight : rect.bottom,
            maxHeight,
        });
    };

    const handleSelect = (item) => {
        onChange?.(item);

        setOpen(false);
        setSearch("");

        setHoveredPath([]);
        setSubmenuPositions({});
    };

    useEffect(() => {
        if (!open) return;
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleOutside = (e) => {
            const menu = document.querySelector(".nested-field-dropdown__root");
            if (
                !wrapperRef.current?.contains(e.target) &&
                !menu?.contains(e.target)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutside);

        return () => {
            document.removeEventListener("mousedown", handleOutside);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
            }
        };

        document.addEventListener("keydown", handleKey);

        return () => {
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    return (
        <div ref={wrapperRef} className="nested-field-dropdown">
            <Dropdown
                fluid
                selection
                open={false}
                text={selectedItem?.display || placeholder}
                title={selectedItem?.display}
                onClick={() => setOpen((v) => !v)}
            />

            {open && (
                <Portal open>
                    <Dropdown.Menu
                        className={`nested-field-dropdown__root ${
                            isError ? "error" : ""
                        }`}
                        onMouseLeave={() => setHoveredPath([])}
                        style={{
                            top: menuPosition.top,
                            left: menuPosition.left,
                            width: menuPosition.width,
                            maxHeight: menuPosition.maxHeight,
                        }}
                    >
                        <div className="nested-field-dropdown__search">
                            <Input
                                fluid
                                icon="search"
                                placeholder="Search..."
                                value={search}
                                onChange={(e, data) => setSearch(data.value)}
                            />
                        </div>

                        {!search && (
                            <MenuLevel
                                items={treeOptions}
                                level={0}
                                position={{
                                    width: menuPosition.width,
                                    left: menuPosition.left,
                                }}
                                onSelect={handleSelect}
                                selectedValue={value}
                                hoveredPath={hoveredPath}
                                setHoveredPath={setHoveredPath}
                                submenuPositions={submenuPositions}
                                setSubmenuPositions={setSubmenuPositions}
                                isError={isError}
                            />
                        )}

                        {!!search && (
                            <div className="nested-field-dropdown__search-results">
                                {!filtered.length && (
                                    <Dropdown.Item className="nested-field-dropdown__empty">
                                        No results
                                    </Dropdown.Item>
                                )}

                                {filtered.map((item) => (
                                    <Dropdown.Item
                                        key={item.value}
                                        className="nested-field-dropdown__search-item"
                                        onMouseDown={(e) => {
                                            e.preventDefault();

                                            handleSelect(item);
                                        }}
                                    >
                                        <ClippedContent popupContent={item.display}>
                                            {highlightMatch(
                                                item?.display ?? "",
                                                search
                                            )}
                                        </ClippedContent>
                                    </Dropdown.Item>
                                ))}
                            </div>
                        )}
                    </Dropdown.Menu>
                </Portal>
            )}
        </div>
    );
};

export default DylFieldSelect;
